import { useRouter } from '@/routing/useAppRouter';
import { useTranslation } from 'next-i18next/pages';
import { useRef } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';

import useCurrentLang from '@/hooks/apps/useCurrentLang';
import { TermsType } from '@/hooks/shared/termsConfig';
import useModal from '@/hooks/shared/useModal';
import useToast from '@/hooks/shared/useToast';

import {
  generateLead,
  trackLeadFormError,
  trackLeadFormStart,
  trackLeadFormSubmit,
} from '@/utils/gtags';
import { readLeadAttribution } from '@/utils/leadAttribution';

import Button from '@/components/shared/ui/buttons/Button';
import ButtonText from '@/components/shared/ui/buttons/ButtonText';
import CheckboxWithHookForm from '@/components/shared/ui/forms/checkbox/CheckboxWithHookForm';
import TermsModal from '@/components/shared/ui/modals/TermsModal';

import InquiryInputs from './InquiryFormInputs';
import { InquiryFormType } from './InquiryFormProvider';
import { selectedInquiryValue } from './inquiryFormValues';
import {
  inquiryInterestRecordValue,
  isInquiryInterest,
} from './inquiryInterest';
import { AI_ANSWER_SOURCE } from './inquirySource';
import {
  INQUIRY_FAILURE_MESSAGE_KEYS,
  type InquirySubmitAction,
  submitInquiryFromBrowser,
} from './inquirySubmission';

type Props = {
  submitAction?: InquirySubmitAction;
};

export default function InquiryForm({
  submitAction = submitInquiryFromBrowser,
}: Props) {
  const { t } = useTranslation('common');

  const currentLang = useCurrentLang();

  const {
    register,
    reset,
    getValues,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useFormContext<InquiryFormType>();

  const { showCustomModal } = useModal();
  const { showToast } = useToast();
  const router = useRouter();
  const hasTrackedFormStart = useRef(false);

  const openTermsModal = (termsType: TermsType) => {
    showCustomModal({
      title: '',
      size: 'sm',
      termsType,
      isResponsive: true,
      component: TermsModal,
    });
  };

  const handleFormFocus = () => {
    if (hasTrackedFormStart.current) return;
    hasTrackedFormStart.current = true;

    trackLeadFormStart({
      attribution: readLeadAttribution(),
      source: getValues('add_data.source'),
    });
  };

  const onSubmit: SubmitHandler<InquiryFormType> = async (values) => {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      agreement,
      employees,
      position,
      interest,
      selfReportedAiService,
      selfReportedSource,
      add_data,
      ...submitValues
    } = values;
    const interestValue = selectedInquiryValue(interest);
    const selectedInterest = isInquiryInterest(interestValue)
      ? interestValue
      : undefined;
    const selectedSource = selectedInquiryValue(selfReportedSource);
    const selectedAiService =
      selectedSource === AI_ANSWER_SOURCE
        ? selectedInquiryValue(selfReportedAiService)
        : undefined;
    const attribution = readLeadAttribution();
    const source = add_data.source;
    const leadContext = {
      attribution,
      interest: selectedInterest,
      selfReportedAiService: selectedAiService,
      selfReportedSource: selectedSource,
      source,
    };

    trackLeadFormSubmit(leadContext);

    const data = {
      ...submitValues,
      bd_id: 'inquiry',
      status: String(3), // 답변 대기상태(3) 설정
      add_data: {
        employees: selectedInquiryValue(employees),
        position,
        interest: selectedInterest
          ? inquiryInterestRecordValue(selectedInterest)
          : undefined,
        marketing: add_data.marketing ? '동의' : '동의 안함',
        language: currentLang,
        self_reported_ai_service: selectedAiService,
        self_reported_source: selectedSource,
        source,
        attribution,
      },
    };

    try {
      const result = await submitAction(data);
      if (!result.success) {
        trackLeadFormError({ ...leadContext, errorType: 'api' });
        showToast({
          state: 'negative',
          message: t(INQUIRY_FAILURE_MESSAGE_KEYS[result.reason]),
        });
        return;
      }

      showToast({
        state: 'positive',
        message: t('문의를 접수했어요. 회신 이메일로 답변드릴게요'),
      });
      generateLead({ ...leadContext, leadId: result.leadId });
      reset();
      router.back();
    } catch (error) {
      console.error('Inquiry submission failed', error);
      trackLeadFormError({ ...leadContext, errorType: 'network' });
      showToast({
        state: 'negative',
        message: t(
          '네트워크 연결 문제로 문의를 보내지 못했어요. 연결을 확인한 뒤 다시 시도해 주세요',
        ),
      });
    }
  };

  return (
    <div>
      <form
        id="inquiry"
        name="inquiry"
        onFocusCapture={handleFormFocus}
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-[40px] lg:items-start lg:gap-[24px]"
      >
        <InquiryInputs />
        <div className="flex flex-col gap-[40px] lg:items-start">
          <div>
            <CheckboxWithHookForm
              {...register('add_data.marketing')}
              data={[
                {
                  id: 'marketing',
                  value: true,
                  label: t(
                    '디플리의 새로운 제품 및 서비스, 이벤트에 대한 정보를 수신합니다.(선택)',
                  ),
                },
              ]}
            />
            <div className="flex items-center">
              <CheckboxWithHookForm
                {...register('agreement', { required: true })}
                data={[
                  {
                    id: 'agreement',
                    value: 'agreement',
                    label: t('개인정보수집 및 이용방침에 동의합니다.'),
                  },
                ]}
              />
              <ButtonText
                type="button"
                size="md"
                variant="underline"
                onClick={() => {
                  openTermsModal('privacy_collect');
                }}
              >
                {t('보기')}
              </ButtonText>
            </div>
          </div>
          <Button
            type="submit"
            color="gray"
            size="lg"
            disabled={!isValid || isSubmitting}
          >
            {t('제출하기')}
          </Button>
        </div>
      </form>
    </div>
  );
}
